import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useMemo } from "react";
import { ArchVizApp } from "@/components/archviz/ArchVizApp";
import { VizHeaderControls } from "@/components/archviz/VizHeaderControls";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useVersionComments } from "@/hooks/use-comments";
import { useVersion } from "@/hooks/use-versions";
import type { ArchitectureData } from "@/lib/arch-parser";
import { useSession } from "@/lib/auth-client";

export function SpecificVersionPage({ slug, versionId }: { slug: string; versionId: string }) {
  const { data, isLoading, isError } = useVersion(slug, versionId);
  const { data: session } = useSession();
  const commentsQuery = useVersionComments(versionId);

  const arch = useMemo(() => {
    if (!data?.parsedData) return null;
    return data.parsedData as ArchitectureData;
  }, [data]);

  const { commentCounts, carryOverCounts } = useMemo(() => {
    const own = new Map<string, number>();
    const carry = new Map<string, number>();
    for (const c of commentsQuery.data?.own ?? []) {
      if (c.resolved) continue;
      own.set(c.elementId, (own.get(c.elementId) ?? 0) + 1);
    }
    for (const c of commentsQuery.data?.carriedOver ?? []) {
      carry.set(c.elementId, (carry.get(c.elementId) ?? 0) + 1);
    }
    return {
      commentCounts: Object.fromEntries(own),
      carryOverCounts: Object.fromEntries(carry),
    };
  }, [commentsQuery.data]);

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (isError || !data || !arch) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">Could not load version.</p>
          <Button variant="outline" asChild>
            <Link to="/solutions/$slug/versions" params={{ slug }}>
              Back to versions
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <ArchVizApp
        architecture={arch}
        readOnly
        extras={{
          versionId,
          commentCounts,
          carryOverCounts,
          currentUserId: session?.user.id ?? null,
        }}
        header={
          <header className="flex items-center gap-3 px-4 py-2.5 border-b bg-card/80 backdrop-blur">
            <Button variant="ghost" size="icon" asChild className="shrink-0">
              <Link to="/solutions/$slug/versions" params={{ slug }}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="min-w-0 shrink-0">
              <div className="text-base truncate">
                v{data.versionNumber}
                {data.label ? ` · ${data.label}` : ""}
                {data.isCurrent && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary">
                    current
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {new Date(data.createdAt).toLocaleString()}
                {data.uploadedByName ? ` · ${data.uploadedByName}` : ""}
              </div>
            </div>
            <span className="h-4 w-px bg-border max-md:hidden" aria-hidden="true" />
            <VizHeaderControls />
          </header>
        }
      />
    </div>
  );
}
